import { useCallback } from 'react';

import { usePriceContext } from 'contexts';
import type { DictionaryKey } from 'dictionary';
import { usePriceFormatter, usePriceTextFormatter } from 'hooks/format';
import type {
	PriceModel,
	PriceType,
	ProductPrice,
	ValueSymbolPrice,
} from 'models/price';
import type { ExtractStrict, HTMLString } from 'types';
import { is } from 'utils/helpers';
import { useI18n } from 'utils/i18n';
import { parseMarkdown } from 'utils/string';

// Excluding 2xs, use PriceText for that.
export type Size = 'lg' | 'md' | 'sm' | 'xl' | 'xs';
export type Theme = 'campaign' | 'julaClub' | 'julaPro' | 'regular';
export type RegularPriceVariant = 'default' | 'graphic';
type LabelAlign = 'left' | 'right';

/**
 * Remove any 'graphical' currency symbols like .- from a string.
 *
 * The frontend knows a bit too much here since the symbol always comes from
 * the backend, but it's necessary for screen readers since the symbol can
 * be read literally like 'period hyphen' in some cases.
 */
function removeCurrencySymbols(text: string): string {
	return text.replaceAll(/[,.:;]-/g, '');
}

/** Get a displayValue/displaySymbol price object from all types of objects. */
export function getValueSymbolPrice(
	price: PriceModel | undefined,
	usePriceExcVat: boolean,
): ValueSymbolPrice | undefined {
	return price
		? 'displayValue' in price
			? price
			: usePriceExcVat
				? 'priceExcVat' in price
					? price.priceExcVat
					: undefined
				: 'priceIncVat' in price
					? price.priceIncVat
					: undefined
		: undefined;
}

/** Is a full product price object. */
export function isProductPrice(price: PriceModel): price is ProductPrice {
	return Boolean(price && typeof price === 'object' && 'fromPrice' in price);
}

/** Get the price type. */
function getPriceType(price: PriceModel): PriceType | undefined {
	return isProductPrice(price) ? price.priceType : undefined;
}

/** Get the main price number. */
export function getPriceValue(price: PriceModel, usePriceExcVat: boolean) {
	return getValueSymbolPrice(price, usePriceExcVat)?.displayValue || undefined;
}

/** Get the currency symbol, if there aren't any decimals. */
export function getCurrency(price: PriceModel, usePriceExcVat: boolean) {
	const p = getValueSymbolPrice(price, usePriceExcVat);
	return p?.displaySymbol && is.integer(p.value) ? p.displaySymbol : undefined;
}

/** Get the price tag theme. */
export function getTheme(price: PriceModel): Theme {
	switch (getPriceType(price)) {
		case 'Sale':
		case 'Clearance':
		case 'Campaign':
			return 'campaign';
		case 'JulaClub':
			return 'julaClub';
		case 'JulaPro':
			return 'julaPro';
		default:
			return 'regular';
	}
}

/** Get the savings price. */
function getSavings(price: PriceModel, usePriceExcVat: boolean) {
	if (!isProductPrice(price)) {
		return undefined;
	}
	if (usePriceExcVat) {
		return price.saveExcVat && price.saveExcVat.displayValue !== '0'
			? price.saveExcVat
			: undefined;
	}
	return price.saveIncVat && price.saveIncVat.displayValue !== '0'
		? price.saveIncVat
		: undefined;
}

/** Check if a label describes a price type. */
function isPriceTypeLabel(label: string | undefined) {
	return is.oneOf(label, 'JulaClub', 'JulaPro');
}

/** Get a label based on the price type. */
function getPriceTypeLabel(price: PriceModel) {
	const priceType = getPriceType(price);
	return isPriceTypeLabel(priceType) ? priceType : undefined;
}

/** Getter for the label text (displayed at the top of the backplate). */
export function useLabelGetter() {
	const { t } = useI18n();
	return useCallback(
		(price: PriceModel) =>
			isProductPrice(price)
				? price.fromPrice
					? t('product_pricetag_from')
					: getPriceTypeLabel(price)
				: undefined,
		[t],
	);
}

/** Label text (displayed at the top of the backplate). */
export function useLabel(price: PriceModel) {
	return useLabelGetter()(price);
}

/** Get the label alignment. */
export function getLabelAlign(label: string | undefined): LabelAlign {
	return isPriceTypeLabel(label) ? 'right' : 'left';
}

/**
 * Get the top tag text (displayed above the backplate).
 *
 * This will only be relevant for club or pro prices that have another label
 * (e.g. 'from'), since the club/pro text is otherwise displayed as the label.
 */
export function getTagTop(price: PriceModel, label?: string) {
	const isNonTypeLabel = label && !isPriceTypeLabel(label);
	const isFromPrice = isProductPrice(price) && price.fromPrice;
	return isNonTypeLabel || isFromPrice ? getPriceTypeLabel(price) : undefined;
}

/** Getter for the bottom tag text (displayed over the backplate's bottom edge). */
export function useTagBottomGetter() {
	const { t } = useI18n();
	const { usePriceExcVat } = usePriceContext();
	const formatPriceText = usePriceTextFormatter();

	return useCallback(
		(price: PriceModel) => {
			if (getTheme(price) === 'regular') {
				return undefined;
			}
			const savings = getSavings(price, usePriceExcVat);
			return savings
				? `${t('product_pricetag_save')} ${formatPriceText(savings.displayValue, savings.displaySymbol)}`
				: undefined;
		},
		[formatPriceText, t, usePriceExcVat],
	);
}

/** Bottom tag text (displayed over the backplate's bottom edge). */
export function useTagBottom(price: PriceModel) {
	return useTagBottomGetter()(price);
}

interface GetScreenReaderTextProps {
	forceRegularPrice?: boolean;
	label?: string;
	price: PriceModel;
}

/** Getter for a screen reader text describing all parts of a price tag. */
export function useScreenReaderTextGetter() {
	const { t } = useI18n();
	const formatPrice = usePriceFormatter();

	const getLabel = useLabelGetter();
	const getTagBottom = useTagBottomGetter();
	const { usePriceExcVat } = usePriceContext();

	return useCallback(
		({
			forceRegularPrice = false,
			label: labelProp,
			price,
		}: GetScreenReaderTextProps) => {
			const label = labelProp || getLabel(price);
			const tagBottom = getTagBottom(price);
			return [
				forceRegularPrice ? '' : getTagTop(price, label),
				[
					// Non-type labels can be things like 'from', which should not have
					// a pause before the price.
					isPriceTypeLabel(label)
						? forceRegularPrice
							? ''
							: `${label},`
						: label,
					`${formatPrice(getPriceValue(price, usePriceExcVat))} ${t('screen_reader_currency_symbol')}`,
				].join(' '),
				// Maybe the component knows a bit too much here, but the bottom tag is
				// often used to display savings where the string contains the currency
				// symbol (e.g. .-) that can be problematic for screen readers.
				// Just remove instead of replacing with a proper currency; the context
				// from the main price should make it clear that this is related and
				// having the full 'euros and cents' equivalent read again is needlessly
				// verbose anyway.
				tagBottom && !forceRegularPrice && removeCurrencySymbols(tagBottom),
			]
				.filter(Boolean)
				.join(', ')
				.trim();
		},
		[t, formatPrice, getLabel, getTagBottom, usePriceExcVat],
	);
}

/** Screen reader text describing all parts of a price tag. */
export function useScreenReaderText(args: GetScreenReaderTextProps) {
	return useScreenReaderTextGetter()(args);
}

// Price types that should NOT display the 'lowest recent price' text, instead
// defaulting to the regular base price text.
const BLOCKED_LOWEST_RECENT_PRICE_TYPES: PriceType[] = [
	'JulaPro',
	'Base',
	'Staff',
];

export type RecentLowestPriceKey = ExtractStrict<
	DictionaryKey,
	| 'product_animation_recent_lowest_price_text'
	| 'product_details_recent_lowest_price_text'
	| 'product_list_recent_lowest_price_text'
>;

export interface GetBasePriceTextProps {
	basePrice: PriceModel | undefined;
	basePriceKey?: 'product_details_base_price_text';
	displayedPriceType: PriceType | undefined;
	recentLowestPriceKey: RecentLowestPriceKey;
}

/**
 * Get different texts describing the base price, depending on the price
 * type and the current market.
 *
 * - A 'recent lowest price' text may exist via the recentLowestPriceKey but
 *   the key may not be set in every market.
 * - The 'recent lowest price' text should only be displayed for certain
 *   price types.
 * - A fallback 'base price' text may exist via the basePriceKey key.
 */
export function useBasePriceTextGetter() {
	const { tEmpty } = useI18n();
	const { usePriceExcVat } = usePriceContext();
	const formatPriceText = usePriceTextFormatter();

	return useCallback(
		({
			basePrice: basePriceProp,
			basePriceKey,
			displayedPriceType,
			recentLowestPriceKey,
		}: GetBasePriceTextProps):
			| { srLabel: string; text: HTMLString }
			| { srLabel?: never; text: string }
			| undefined => {
			const basePrice = getValueSymbolPrice(basePriceProp, usePriceExcVat);
			if (!basePrice || !displayedPriceType) {
				return undefined;
			}
			const tParams = {
				basePrice: formatPriceText(
					basePrice?.displayValue,
					basePrice?.displaySymbol,
				),
			};

			// Key is not set in every market, in which case the base text should be
			// displayed instead if a key for that is supplied.
			const lowestRecentText = tEmpty(recentLowestPriceKey, tParams);
			const text =
				lowestRecentText &&
				!BLOCKED_LOWEST_RECENT_PRICE_TYPES.includes(displayedPriceType)
					? lowestRecentText
					: basePriceKey
						? tEmpty(basePriceKey, tParams)
						: undefined;

			if (!text) {
				return undefined;
			}

			const parsedText = parseMarkdown(text, { inline: true });
			const srLabel = tEmpty(
				'product_recent_lowest_price_screen_reader_label',
				tParams,
			);
			return {
				text: parsedText,
				// If the text contains any HTML, make a separate screen reader label
				// available to avoid having the HTML punctuation read out loud. Also
				// remove currency symbols like .- since they can also be read verbatim.
				// Surrounding context should make it clear that the number is a price.
				srLabel:
					srLabel && /<[^>]+?>/i.test(parsedText)
						? removeCurrencySymbols(srLabel)
						: '',
			};
		},
		[formatPriceText, tEmpty, usePriceExcVat],
	);
}

/** Base price text (displayed below the backplate). */
export function useBasePriceText(args: GetBasePriceTextProps) {
	return useBasePriceTextGetter()(args);
}

/** Check if a price is discounted in some way. */
export function hasReducedPrice(priceType: PriceType | undefined) {
	return is.oneOf(
		priceType,
		'Campaign',
		'Clearance',
		'JulaClub',
		'JulaPro',
		'Sale',
	);
}

export function isPriceLowerThanBasePrice({
	basePrice,
	price,
	usePriceExcVat,
}: {
	basePrice: PriceModel | undefined;
	price: PriceModel | undefined;
	usePriceExcVat: boolean;
}) {
	const formattedBasePrice = getValueSymbolPrice(basePrice, usePriceExcVat);
	const formattedPrice = getValueSymbolPrice(price, usePriceExcVat);
	return formattedBasePrice && formattedPrice
		? formattedBasePrice.value > formattedPrice.value
		: false;
}
